// LocalDeEstoqueModal.tsx
//
// Cadastro de LOCAL DE ESTOQUE (Suprimentos): a prateleira, a câmara fria, o
// depósito. Sempre DENTRO de um ambiente de uma loja.
//
// POR QUE A AMARRAÇÃO AO AMBIENTE É O PONTO: saldo de estoque sem lugar é um
// número que não responde pergunta nenhuma. Com o local amarrado ao ambiente, a
// rede consegue responder "qual é o saldo de massa no estoque da cozinha da
// loja X" — e, com isso, saber onde falta e de onde transferir. Sem isso,
// sobra um total da loja que não diz em que canto o insumo está. O tipo de
// ambiente "Estoque" (ver `getTypeBadge` em Ambients.tsx: Atendimento,
// Produção, Logística, Administrativo, Estoque) existe exatamente para isto.
//
// ONDE GRAVA: `POST/PATCH suprimentos/locais-de-estoque`, a tabela
// `asup."LocalDeEstoque"`. Quem monta a requisição é o `handleSave` da
// Configuração — por isso o payload daqui usa OS NOMES DE COLUNA do agregado
// (`subsidiary`, `ambient`, `nome`, `tipo`) e não nomes inventados: o servidor
// descarta em silêncio toda chave fora da lista branca do agregado, e
// `subsidiary`, `ambient` e `nome` são NOT NULL — cair fora da lista branca
// derruba o INSERT.
//
// Não existe tabela desta lista na tela ainda: o local de estoque é cadastrado
// aqui e lido pelo REST paginado, não pelo InfoToken.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface ModalPadraoProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

/** O tipo de ambiente feito para guardar insumo. */
const AMBIENTE_DE_ESTOQUE = "Estoque";

const LocalDeEstoqueModal: React.FC<ModalPadraoProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-localdeestoquemodal",
  });

  // `{}` é truthy (useModal faz `setSelectedObject(object || {})`): quem diz se
  // é edição é o uuid, não a existência do objeto. `localUuid` vem primeiro
  // porque é a chave que o `handleSave` do shell lê para escolher entre POST e
  // PATCH — divergir dela faria o modal dizer "Editar" enquanto o shell criava
  // uma linha nova.
  const uuidEmEdicao =
    selectedObject?.localUuid ||
    selectedObject?.localEstoqueUuid ||
    selectedObject?.localEstoqueUUID ||
    "";
  const ehEdicao = Boolean(uuidEmEdicao);
  const tipoDoModal = ehEdicao ? "updateLocalEstoque" : "createLocalEstoque";

  // Os campos têm o nome DA COLUNA do agregado `locais-de-estoque`.
  const [form, setForm] = useState({
    subsidiary: "",
    ambient: "",
    nome: "",
    tipo: "",
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /** As lojas reais: organization -> companies -> subsidiaries. */
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
      }))
    );
  }, [decryptedInfoToken]);

  /**
   * Os ambientes DA LOJA ESCOLHIDA, e só dela.
   *
   * Mostrar os ambientes de toda a rede deixaria escolher "Cozinha" da loja
   * errada — e o saldo passaria a ser contado num lugar que não existe naquela
   * loja. Enquanto não houver loja escolhida, a lista fica vazia de propósito.
   */
  const ambientesDaLoja: any[] = useMemo(() => {
    if (!form.subsidiary) return [];
    const loja = lojas.find(
      (filial: any) => filial.subsidiaryUuid === form.subsidiary
    );
    return loja?.ambients ?? [];
  }, [lojas, form.subsidiary]);

  const temAmbienteDeEstoque = ambientesDaLoja.some(
    (ambiente: any) => ambiente?.ambientType === AMBIENTE_DE_ESTOQUE
  );

  useEffect(() => {
    const cru = selectedObject?.localEstoqueInfoValue;
    const valores =
      cru && typeof parseInfoValue === "function"
        ? { ...selectedObject, ...parseInfoValue(cru) }
        : selectedObject ?? {};

    setForm({
      subsidiary: valores.subsidiary || valores.subsidiaryUuid || "",
      ambient: valores.ambient || valores.ambientUuid || "",
      nome: valores.nome || valores.localEstoqueNome || "",
      tipo: valores.tipo || valores.localEstoqueTipo || "",
    });
    setAviso(null);
  }, [selectedObject, parseInfoValue]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  /**
   * Trocar de loja zera o ambiente.
   *
   * Sem isto o campo continuaria com o uuid de um ambiente da loja ANTERIOR —
   * invisível no select (que só lista os da loja nova) e ainda assim enviado no
   * salvamento. Seria um local de estoque apontando para o ambiente de outra
   * loja, e ninguém veria na tela.
   */
  const handleTrocaDeLoja = (subsidiary: string) => {
    setForm((anterior) => ({ ...anterior, subsidiary, ambient: "" }));
  };

  const validar = (): string | null => {
    if (!form.subsidiary) return "Escolha a loja do local de estoque.";
    if (!form.ambient)
      return "Escolha o ambiente onde este local de estoque fica.";
    if (!form.nome.trim()) return "O nome do local de estoque é obrigatório.";
    return null;
  };

  const handleSubmit = async () => {
    const erro = validar();
    if (erro) {
      setAviso({ tom: "erro", texto: erro });
      return;
    }

    setSalvando(true);
    try {
      const r = await handleSave(tipoDoModal, {
        ...form,
        nome: form.nome.trim(),
        tipo: form.tipo.trim(),
        localUuid: uuidEmEdicao || undefined,
      });
      if (r && !r.ok && montado.current) {
        setAviso({ tom: r.tom || "erro", texto: r.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo}>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-localdeestoquemodal">
            <AlfredoStyles.MaterialIcons>
              inventory_2
            </AlfredoStyles.MaterialIcons>
            {ehEdicao
              ? "Editar Local de Estoque"
              : "Novo Local de Estoque"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            <AlfredoStyles.AlertMessage $tom="atencao">
              O local de estoque nasce <strong>dentro de um ambiente</strong> —
              e é isso que faz o saldo virar resposta: sem local de estoque não
              há como responder "qual é o saldo de massa no estoque da cozinha
              da loja X", só um total da loja que não diz em que canto o insumo
              está. O tipo de ambiente <strong>Estoque</strong> existe
              justamente para isso.
            </AlfredoStyles.AlertMessage>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>Onde fica</AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Loja *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.subsidiary}
                    onChange={(e) => handleTrocaDeLoja(e.target.value)}
                  >
                    <option value="">Selecione a loja</option>
                    {lojas.map((loja: any) => (
                      <option
                        key={loja.subsidiaryUuid}
                        value={loja.subsidiaryUuid}
                      >
                        {loja.subsidiaryName}
                        {loja.companyName ? ` — ${loja.companyName}` : ""}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Ambiente *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.ambient}
                    disabled={!form.subsidiary}
                    onChange={(e) =>
                      handleLocalChange("ambient", e.target.value)
                    }
                  >
                    <option value="">
                      {form.subsidiary
                        ? "Selecione o ambiente"
                        : "Escolha a loja primeiro"}
                    </option>
                    {ambientesDaLoja.map((ambiente: any) => (
                      <option
                        key={ambiente.ambientUuid}
                        value={ambiente.ambientUuid}
                      >
                        {ambiente.ambientName}
                        {ambiente.ambientType
                          ? ` (${ambiente.ambientType})`
                          : ""}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              {form.subsidiary && ambientesDaLoja.length === 0 && (
                <AlfredoStyles.AlertMessage $tom="erro">
                  Esta loja não tem nenhum ambiente cadastrado. Cadastre o
                  ambiente primeiro, na aba Ambientes de Lojas — o local de
                  estoque só existe dentro de um.
                </AlfredoStyles.AlertMessage>
              )}

              {form.subsidiary &&
                ambientesDaLoja.length > 0 &&
                !temAmbienteDeEstoque && (
                  <AlfredoStyles.AlertMessage $tom="atencao">
                    Nenhum ambiente desta loja é do tipo{" "}
                    <strong>Estoque</strong>. Dá para amarrar o local a um
                    ambiente de outro tipo (uma cozinha também guarda insumo),
                    mas se a intenção é um depósito, o tipo certo do ambiente é
                    Estoque.
                  </AlfredoStyles.AlertMessage>
                )}
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Dados do local
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      handleLocalChange("nome", e.target.value)
                    }
                    placeholder="Ex.: Câmara fria, Prateleira A, Depósito"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Tipo</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.tipo}
                    onChange={(e) =>
                      handleLocalChange("tipo", e.target.value)
                    }
                    placeholder="Ex.: refrigerado, seco, congelado"
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.AlertMessage $tom="atencao">
              O local de estoque grava em{" "}
              <strong>suprimentos/locais-de-estoque</strong>. O{" "}
              <strong>ambiente</strong> escolhido acima é outra coisa e vem de
              outro lugar: ele é criado na aba Ambientes de Lojas
              (<code>POST /ambient</code>), e este formulário só aponta para
              ele. Nenhum ambiente é criado aqui.
            </AlfredoStyles.AlertMessage>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando..."
                : ehEdicao
                ? "Atualizar local"
                : "Criar local"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default LocalDeEstoqueModal;
